import type { InitialWebsiteGenerationNotificationArmedData } from '@/modules/EventEmitter/const';
import { useCallback, useEffect, useState } from 'react';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import { useProjectStore } from '@/stores/project';
import { useUserStore } from '@/stores/user';
import {
  getInitialGenerationNotificationOwnerId,
  getSystemNotificationPermission,
  getSystemNotificationPreference,
  requestSystemNotificationPermissionOnce,
  setSystemNotificationPreference,
} from './helper';

interface PermissionGuideState {
  audienceId: string;
  projectId: string;
}

export function useInitialWebsiteGenerationPermissionGuide() {
  const projectId = useProjectStore(state => state.projectId);
  const userId = useUserStore(state => state.userInfo?.userId);
  const [guideState, setGuideState] = useState<PermissionGuideState | null>(null);

  const showPermissionGuide = useCallback(
    (targetProjectId: string, targetUserId?: string) => {
      const audienceId = getInitialGenerationNotificationOwnerId(targetProjectId, targetUserId || userId);
      if (getSystemNotificationPermission() !== 'default') {
        return;
      }
      if (getSystemNotificationPreference(audienceId) === false) {
        return;
      }

      setGuideState({
        audienceId,
        projectId: targetProjectId,
      });
    },
    [userId],
  );

  useEffect(() => {
    const handleNotificationArmed = (payload: InitialWebsiteGenerationNotificationArmedData) => {
      if (!payload.projectId || (projectId && payload.projectId !== projectId)) {
        return;
      }

      showPermissionGuide(payload.projectId);
    };

    globalEventEmitter.on(EVENTS.INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED, handleNotificationArmed);
    return () => {
      globalEventEmitter.off(EVENTS.INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED, handleNotificationArmed);
    };
  }, [projectId, showPermissionGuide]);

  useEffect(() => {
    setGuideState(current => (current && current.projectId === projectId ? current : null));
  }, [projectId]);

  const handleConfirm = useCallback(() => {
    if (!guideState) {
      return;
    }

    const { audienceId } = guideState;
    setGuideState(null);
    void requestSystemNotificationPermissionOnce(audienceId, { ignorePrompted: true }).then(result => {
      setSystemNotificationPreference(audienceId, result.permission === 'granted');
    });
  }, [guideState]);

  const handleCancel = useCallback(() => {
    setGuideState(null);
  }, []);

  return {
    visible: Boolean(guideState),
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };
}
